/**
 * SEO / GEO single source of truth.
 *
 * Canonical host, default social-card copy, and JSON-LD builders live here so
 * the app, the static `index.html` baseline, and the build-time sitemap all
 * agree on one origin and one brand identity.
 *
 * NOTE on rendering: 2g1p is a client-rendered SPA today. The tags emitted by
 * `useSeo` are read by crawlers that execute JS (Googlebot), but NOT by social
 * scrapers or most AI crawlers, which only see the initial HTML. The static
 * baseline in `index.html` covers those for the site root. Per-page cards
 * become fully effective once routes are prerendered/SSR'd (the deferred
 * Tier-0 step) — this module is written to need no changes when that lands.
 */

/** Canonical production origin (no trailing slash). Override per-env if needed. */
export const SITE_URL: string = (
  (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://2girls1parlay.com'
).replace(/\/$/, '');

export const SITE_NAME = '2 Girls 1 Parlay';

/** Default social-share image (1024×1024 cover art in /public). */
export const DEFAULT_OG_IMAGE = '/0_MAIN.png';
export const DEFAULT_OG_IMAGE_DIMS = { width: 1024, height: 1024 };

export const DEFAULT_DESCRIPTION =
  'Two sisters, every slate, receipts included. AI-backed breakdowns, honest ' +
  'parlays, and an auto-graded track record across the NBA, MLB, NHL and NFL. ' +
  'We explain the bets — we do not sell picks.';

/** Twitter/X handle for card attribution, if/when the account is live. */
export const TWITTER_HANDLE = ''; // e.g. '@2girls1parlay' once the X account exists

/** Build an absolute URL from a root-relative path (or pass through absolutes). */
export function absoluteUrl(path: string = '/'): string {
  if (/^https?:\/\//i.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
}

// --------------------------------------------------------------- JSON-LD builders
//
// Kept honest on purpose: the Organization's authority signal is the
// *verifiable, auto-graded record*, not claimed human credentials. Bylines are
// pen names (a standard, legal editorial practice), so Article authors are
// modeled as Person with the pen name and the Organization as publisher — we
// never assert professional/financial credentials for them.

export interface JsonLd {
  '@context': 'https://schema.org';
  '@type': string;
  [key: string]: unknown;
}

export function organizationSchema(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: absoluteUrl('/favicon.svg'),
    description:
      'An independent sports-betting publication: honest explainers, AI-backed ' +
      'matchup breakdowns, and a publicly graded prediction record. Not a ' +
      'sportsbook and not a paid-pick service.',
    sameAs: ['https://www.instagram.com/2girls_1parlay/'],
  };
}

export function websiteSchema(): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    publisher: { '@type': 'Organization', name: SITE_NAME },
  };
}

export interface ArticleSchemaInput {
  title: string;
  description: string;
  path: string;
  datePublished: string; // 'YYYY-MM-DD'
  authorName?: string; // pen name
  image?: string;
}

export function articleSchema(input: ArticleSchemaInput): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: input.title,
    description: input.description,
    url: absoluteUrl(input.path),
    mainEntityOfPage: absoluteUrl(input.path),
    datePublished: input.datePublished,
    image: absoluteUrl(input.image ?? DEFAULT_OG_IMAGE),
    ...(input.authorName
      ? { author: { '@type': 'Person', name: input.authorName } }
      : {}),
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: absoluteUrl('/favicon.svg') },
    },
  };
}

export interface FaqEntry {
  q: string;
  a: string;
}

/** FAQPage schema — strong GEO citation bait when answers are honest + direct. */
export function faqPageSchema(entries: FaqEntry[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: entries.map((e) => ({
      '@type': 'Question',
      name: e.q,
      acceptedAnswer: { '@type': 'Answer', text: e.a },
    })),
  };
}

export interface Crumb {
  name: string;
  path: string;
}

export function breadcrumbSchema(crumbs: Crumb[]): JsonLd {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: absoluteUrl(c.path),
    })),
  };
}
